"use client";

import React, { useState, useEffect, useRef } from 'react';
import mqtt, { IClientOptions, MqttClient } from 'mqtt';
import { 
  Box,
  Heading,
  Input,
  Button,
  Badge,
  Text,
  VStack,
  HStack,
  Flex,
  Textarea,
  IconButton,
  useColorModeValue,
  Grid,
  GridItem,
  Divider,
  useToast,
} from '@chakra-ui/react';
import { FiPlus, FiSend, FiTrash } from 'react-icons/fi';
import { getSha256 } from '@/utils/utils';

interface Message {
  topic: string;
  payload: string;
  timestamp: Date;
}

// MQTT Manager class to handle connection and messaging
class MQTTManager {
  private client: MqttClient | null = null;
  private connectionString: string;
  private clientId: string;
  private username: string;
  private password: string;
  private onMessageCallback: ((topic: string, payload: string) => void) | null = null;
  private onConnectCallback: (() => void) | null = null;
  private onDisconnectCallback: (() => void) | null = null;

  constructor(connectionString: string, clientId: string, username: string, password: string) {
    this.connectionString = connectionString;
    this.clientId = clientId;
    this.username = username;
    this.password = password;
  }

  connect() {
    if (this.client) {
      console.log('Client already exists, disconnecting first');
      this.disconnect();
    }

    const options: IClientOptions = {
      clientId: this.clientId,
      clean: true,
      reconnectPeriod: 0,
      username: this.username,
      password: this.password,
    };

    console.log(`Connecting to ${this.connectionString} as ${this.clientId}`);
    this.client = mqtt.connect(this.connectionString, options);

    this.client.on('connect', () => {
      console.log('MQTT connected successfully');
      if (this.onConnectCallback) this.onConnectCallback();
    });

    this.client.on('message', (topic: string, payload: Buffer) => {
      if (this.onMessageCallback) {
        this.onMessageCallback(topic, payload.toString());
      }
    });

    this.client.on('error', (err: Error) => {
      console.error('MQTT connection error:', err);
    });

    this.client.on('close', () => {
      console.log('MQTT connection closed');
      if (this.onDisconnectCallback) this.onDisconnectCallback();
    });
  }

  disconnect() {
    if (this.client) {
      try {
        this.client.end(true);
        this.client = null;
        console.log('MQTT disconnected');
      } catch (err) {
        console.error('Error disconnecting MQTT:', err);
      }
    }
  }

  subscribe(topic: string, callback?: (err: Error | null) => void) {
    if (!this.client || !this.client.connected) {
      console.error('Cannot subscribe: MQTT client not connected');
      return;
    }
    
    this.client.subscribe(topic, (err: Error | null) => {
      if (err) {
        console.error(`Error subscribing to ${topic}:`, err);
      } else {
        console.log(`Subscribed to ${topic}`);
      }
      if (callback) callback(err);
    });
  }

  unsubscribe(topic: string) {
    if (!this.client || !this.client.connected) return;
    this.client.unsubscribe(topic);
  }

  publish(topic: string, message: string) {
    if (!this.client || !this.client.connected) {
      console.error('Cannot publish: MQTT client not connected');
      return;
    }
    this.client.publish(topic, message);
  }

  onMessage(callback: (topic: string, payload: string) => void) {
    this.onMessageCallback = callback;
  }

  onConnect(callback: () => void) {
    this.onConnectCallback = callback;
  }

  onDisconnect(callback: () => void) {
    this.onDisconnectCallback = callback;
  }

  isConnected() {
    return this.client?.connected || false;
  }
}

const MqttUI: React.FC = () => {
  const toast = useToast();
  const [connected, setConnected] = useState(false);
  const [subscriptions, setSubscriptions] = useState<string[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newTopic, setNewTopic] = useState('parky/+/+/status');
  const [publishTopic, setPublishTopic] = useState('parky/device/867255072966163/command');
  const [publishMessage, setPublishMessage] = useState('{"command":"info","hardwareId":"867255072966163"}');
  const [connectionString, setConnectionString] = useState('wss://mqtt.letsparky.com');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const mqttManagerRef = useRef<MQTTManager | null>(null);

  // Color mode values
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const user_instance_hash = getSha256(
    window.navigator.userAgent +
    window.navigator.hardwareConcurrency +
    window.navigator.language
  );

  // Initialize MQTT manager without auto-connecting
  useEffect(() => {
    // Create MQTT manager
    const manager = new MQTTManager(
      connectionString,
      `panel_${user_instance_hash}`,
      "consumer",
      "rc>2uELJ9Av*"
    );
    
    // Set up callbacks
    manager.onConnect(() => {
      setConnected(true);
      toast({
        title: 'MQTT Connected',
        description: 'Successfully connected to MQTT broker',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    });
    
    manager.onDisconnect(() => {
      setConnected(false);
      toast({
        title: 'MQTT Disconnected',
        description: 'Disconnected from MQTT broker',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    });
    
    manager.onMessage((topic, payload) => {
      const message = {
        topic,
        payload,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, message]);
    });
    
    // Store reference without connecting
    mqttManagerRef.current = manager;
    
    // Cleanup on unmount
    return () => {
      if (mqttManagerRef.current) {
        mqttManagerRef.current.disconnect();
        mqttManagerRef.current = null;
      }
    };
  }, [connectionString, toast]);

  // Handle subscriptions
  useEffect(() => {
    if (!mqttManagerRef.current || !connected) return;
    
    // Subscribe to all topics
    subscriptions.forEach(topic => {
      mqttManagerRef.current?.subscribe(topic);
    });
    
  }, [connected, subscriptions]);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSubscribe = () => {
    if (!newTopic || !mqttManagerRef.current || subscriptions.includes(newTopic)) return;
    
    mqttManagerRef.current.subscribe(newTopic, (err) => {
      if (!err) {
        setSubscriptions([...subscriptions, newTopic]);
        toast({
          title: 'Subscribed',
          description: `Successfully subscribed to ${newTopic}`,
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Subscription Failed',
          description: `Failed to subscribe to ${newTopic}`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    });
  };

  const handleUnsubscribe = (topic: string) => {
    if (!mqttManagerRef.current) return;
    
    mqttManagerRef.current.unsubscribe(topic);
    setSubscriptions(subscriptions.filter(t => t !== topic));
    toast({
      title: 'Unsubscribed',
      description: `Unsubscribed from ${topic}`,
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  const handlePublish = () => {
    if (!publishTopic || !mqttManagerRef.current) return;
    
    mqttManagerRef.current.publish(publishTopic, publishMessage);
    toast({
      title: 'Message Published',
      description: `Published message to ${publishTopic}`,
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const clearMessages = () => {
    setMessages([]);
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString();
  };

  const handleConnect = () => {
    if (mqttManagerRef.current && !connected) {
      mqttManagerRef.current.connect();
    }
  };

  const handleDisconnect = () => {
    if (mqttManagerRef.current) {
      mqttManagerRef.current.disconnect();
      setConnected(false);
    }
  };

  return (
    <Box>
      <Heading size="lg" mb={6}>MQTT Dashboard</Heading>
      
      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6}>
        {/* Connection and Subscription Panel */}
        <GridItem>
          <VStack spacing={6} align="stretch">
            {/* Connection Card */}
            <Box
              borderWidth="1px"
              borderRadius="lg"
              borderColor={borderColor}
              bg={bg}
              p={6}
            >
              <Flex justify="space-between" align="center" mb={4}>
                <Heading size="md">Connection</Heading>
                <Badge 
                  colorScheme={connected ? "green" : "red"}
                  variant="solid"
                >
                  {connected ? "Connected" : "Disconnected"}
                </Badge>
              </Flex>
              
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>Broker URL</Text>
                  <HStack>
                    <Input 
                      value={connectionString} 
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConnectionString(e.target.value)}
                      placeholder="mqtt://localhost:1883"
                      isDisabled={connected}
                    />
                    {connected ? (
                      <Button 
                        onClick={handleDisconnect}
                        colorScheme="red"
                        minW="120px"
                      >
                        Disconnect
                      </Button>
                    ) : (
                      <Button 
                        onClick={handleConnect}
                        colorScheme="blue"
                        minW="120px"
                      >
                        Connect
                      </Button>
                    )}
                  </HStack>
                </Box>
              </VStack>
            </Box>

            {/* Subscriptions Card */}
            <Box
              borderWidth="1px"
              borderRadius="lg"
              borderColor={borderColor}
              bg={bg}
              p={6}
            >
              <Heading size="md" mb={4}>Subscriptions</Heading>
              
              <VStack spacing={4} align="stretch">
                <HStack>
                  <Input 
                    value={newTopic} 
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewTopic(e.target.value)}
                    placeholder="Enter topic to subscribe"
                    isDisabled={!connected}
                    onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && handleSubscribe()}
                  />
                  <Button 
                    onClick={handleSubscribe} 
                    isDisabled={!connected || !newTopic}
                    colorScheme="blue"
                    leftIcon={<FiPlus />}
                  >
                    Subscribe
                  </Button>
                </HStack>
                
                <VStack spacing={2} align="stretch">
                  {subscriptions.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">No active subscriptions</Text>
                  ) : (
                    subscriptions.map((topic) => (
                      <Flex key={topic} justify="space-between" align="center" bg={cardBg} p={3} borderRadius="md">
                        <Text fontSize="sm" fontFamily="mono">{topic}</Text>
                        <IconButton 
                          size="sm" 
                          variant="ghost" 
                          aria-label="Unsubscribe"
                          icon={<FiTrash />}
                          colorScheme="red"
                          onClick={() => handleUnsubscribe(topic)}
                        />
                      </Flex>
                    ))
                  )}
                </VStack>
              </VStack>
            </Box>

            {/* Publish Card */}
            <Box
              borderWidth="1px"
              borderRadius="lg"
              borderColor={borderColor}
              bg={bg}
              p={6}
            >
              <Heading size="md" mb={4}>Publish Command</Heading>
              
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>Topic</Text>
                  <Input 
                    value={publishTopic} 
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPublishTopic(e.target.value)}
                    placeholder="Enter topic to publish to"
                    isDisabled={!connected}
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>Message</Text>
                  <Textarea 
                    value={publishMessage} 
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setPublishMessage(e.target.value)}
                    placeholder="Enter message to publish"
                    isDisabled={!connected}
                    rows={3}
                  />
                </Box>
                <Button 
                  onClick={handlePublish} 
                  isDisabled={!connected || !publishTopic}
                  colorScheme="green"
                  leftIcon={<FiSend />}
                >
                  Publish
                </Button>
              </VStack>
            </Box>
          </VStack>
        </GridItem>

        {/* Messages Panel */}
        <GridItem>
          <Box
            borderWidth="1px"
            borderRadius="lg"
            borderColor={borderColor}
            bg={bg}
            p={6}
            h="calc(100vh - 200px)"
            display="flex"
            flexDirection="column"
          >
            <Flex justify="space-between" align="center" mb={4}>
              <Heading size="md">Incoming Messages</Heading>
              <Button 
                size="sm" 
                variant="outline"
                onClick={clearMessages}
                isDisabled={messages.length === 0}
                leftIcon={<FiTrash />}
              >
                Clear
              </Button>
            </Flex>
            
            <Box 
              flex="1" 
              overflowY="auto" 
              borderWidth="1px"
              borderRadius="md" 
              p={4} 
              bg={cardBg}
            >
              {messages.length === 0 ? (
                <Flex h="full" align="center" justify="center">
                  <Text color="gray.500">No messages received yet</Text>
                </Flex>
              ) : (
                <VStack spacing={3} align="stretch">
                  {messages.map((msg, index) => (
                    <Box key={index} bg={bg} borderWidth="1px" borderColor={borderColor} p={3} borderRadius="md" shadow="sm">
                      <Flex justify="space-between" align="start" mb={2}>
                        <Text fontSize="sm" fontWeight="medium" color="blue.500" fontFamily="mono">{msg.topic}</Text>
                        <Text fontSize="xs" color="gray.500">{formatTime(msg.timestamp)}</Text>
                      </Flex>
                      <Box bg={cardBg} p={2} borderRadius="md" overflowX="auto">
                        <Text fontSize="sm" fontFamily="mono" whiteSpace="pre-wrap" wordBreak="break-word">
                          {msg.payload}
                        </Text>
                      </Box>
                    </Box>
                  ))}
                  <div ref={messagesEndRef} />
                </VStack>
              )}
            </Box>
          </Box>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default MqttUI;